/**
 * Typography Section - Font testing and comparison
 *
 * Compares:
 * - Current: Standard font weights, normal hierarchy
 * - iOS-Style: Bolder weights, stronger hierarchy, tighter spacing
 */
export default function TypographySection({ theme }) {
  const isDark = theme === 'dark';

  const scales = [
    { label: 'Display Large', current: 'text-4xl font-bold', ios: 'text-4xl font-black tracking-tight' },
    { label: 'Display', current: 'text-3xl font-bold', ios: 'text-3xl font-black tracking-tight' },
    { label: 'Heading 1', current: 'text-2xl font-semibold', ios: 'text-2xl font-bold tracking-tight' },
    { label: 'Heading 2', current: 'text-xl font-semibold', ios: 'text-xl font-bold' },
    { label: 'Heading 3', current: 'text-lg font-medium', ios: 'text-lg font-semibold' },
    { label: 'Body Large', current: 'text-base font-normal', ios: 'text-base font-medium' },
    { label: 'Body', current: 'text-sm font-normal', ios: 'text-sm font-normal' },
    { label: 'Caption', current: 'text-xs font-normal', ios: 'text-xs font-medium' },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h2 className={`text-2xl font-bold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
          Typography Comparison
        </h2>
        <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
          Current design vs iOS-inspired bolder typography
        </p>
      </div>

      {/* Comparison Grid */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Current Design */}
        <div className={`p-6 rounded-lg border-2 ${
          isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
        }`}>
          <div className={`text-xs font-semibold uppercase tracking-wider mb-4 ${
            isDark ? 'text-gray-400' : 'text-gray-500'
          }`}>
            Current Design
          </div>
          <div className="space-y-4">
            {scales.map(scale => (
              <div key={scale.label} className="border-b border-gray-200 dark:border-gray-700 pb-3">
                <div className={`text-xs mb-1 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                  {scale.label}
                </div>
                <div className={`${scale.current} ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  The quick brown fox
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* iOS-Style Design */}
        <div className={`p-6 rounded-lg border-2 ${
          isDark ? 'bg-gray-800 border-blue-500' : 'bg-white border-blue-300'
        }`}>
          <div className={`text-xs font-bold uppercase tracking-wider mb-4 ${
            isDark ? 'text-blue-400' : 'text-blue-600'
          }`}>
            iOS-Style (Enhanced)
          </div>
          <div className="space-y-4">
            {scales.map(scale => (
              <div key={scale.label} className="border-b border-gray-200 dark:border-gray-700 pb-3">
                <div className={`text-xs mb-1 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                  {scale.label}
                </div>
                <div className={`${scale.ios} ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  The quick brown fox
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Full Text Example */}
      <div className="grid md:grid-cols-2 gap-6">
        <div className={`p-6 rounded-lg ${isDark ? 'bg-gray-800' : 'bg-white'}`}>
          <h3 className={`text-xl font-semibold mb-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>
            Current Paragraph
          </h3>
          <p className={`text-sm leading-relaxed ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
            The maritime safety management system ensures compliance with international regulations.
            All vessel equipment must be maintained according to manufacturer specifications and
            regulatory requirements. Regular inspections help identify potential issues before
            they become critical failures.
          </p>
        </div>

        <div className={`p-6 rounded-lg border-2 ${
          isDark ? 'bg-gray-800 border-blue-500' : 'bg-white border-blue-200'
        }`}>
          <h3 className={`text-xl font-bold mb-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>
            iOS-Style Paragraph
          </h3>
          <p className={`text-sm font-medium leading-relaxed ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>
            The maritime safety management system ensures compliance with international regulations.
            All vessel equipment must be maintained according to manufacturer specifications and
            regulatory requirements. Regular inspections help identify potential issues before
            they become critical failures.
          </p>
        </div>
      </div>
    </div>
  );
}
